import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import Index from './pages/Index';
import NotFound from './pages/NotFound';
import LandingPage from './pages/LandingPage';
import ForgePage from './pages/ForgePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AuthHandler from './pages/AuthHandler';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ActionProvider } from './contexts/ActionContext';
import { GameStateProvider } from './contexts/GameStateContext';
import { MaintenanceProvider } from './contexts/MaintenanceContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Admin pages
import AdminDashboard from './admin/pages/AdminDashboard';
import UserManagement from './admin/pages/UserManagement';
import ReportManagement from './admin/pages/ReportManagement';
import WordAnalytics from './admin/pages/WordAnalytics';
import ConceptMap from './admin/pages/ConceptMap';
import BannedWords from './admin/pages/BannedWords';

import { AdminProvider, useAdmin } from './admin/contexts/AdminContext';

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ThemeProvider attribute="class">
        <TooltipProvider>
          <Toaster />
          <AuthProvider>
            <ActionProvider>
              <GameStateProvider>
                <MaintenanceProvider>
                  <AdminProvider>
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route
                        path="/forge"
                        element={
                          <ProtectedRoute>
                            <ForgePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/legacy" element={<Index />} />
                      <Route
                        path="/payment-success"
                        element={
                          <ProtectedRoute>
                            <PaymentSuccessPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin Routes */}
                      <Route
                        path="/admin"
                        element={
                          <AdminRoute>
                            <AdminDashboard />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/users"
                        element={
                          <AdminRoute>
                            <UserManagement />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/reports"
                        element={
                          <AdminRoute>
                            <ReportManagement />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/words"
                        element={
                          <AdminRoute>
                            <WordAnalytics />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/concept-map"
                        element={
                          <AdminRoute>
                            <ConceptMap />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/banned-words"
                        element={
                          <AdminRoute>
                            <BannedWords />
                          </AdminRoute>
                        }
                      />

                      {/* Privacy Policy - accessible without login */}
                      <Route path="/privacy" element={<PrivacyPolicy />} />

                      {/* Auth handler routes - these are important for handling Firebase auth redirects */}
                      <Route path="/__/auth/handler" element={<AuthHandler />} />
                      <Route path="/auth-handler" element={<AuthHandler />} />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </AdminProvider>
                </MaintenanceProvider>
              </GameStateProvider>
            </ActionProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

// ProtectedRoute component is now imported from './components/auth/ProtectedRoute'

// AdminRoute component
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { currentUser } = useAuth();
  const { isAdmin, isLoading } = useAdmin();

  // If not logged in, redirect to login
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // Show loading while checking admin status
  if (isLoading) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        <p className="mt-4 text-muted-foreground">Verifying admin access...</p>
      </div>
    );
  }

  // If not an admin, redirect to home
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default App;
